import { motion } from 'framer-motion'
import { useReducedMotion } from 'framer-motion'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'

import {
  mergeReducedMotionPreset,
  resolveTabIndicatorTransition,
  useThemeMotion,
} from '../../animation/themeMotion'
import {
  type ComicPageRegenerateMode,
} from '../../api/comicEpisodeJobs'
import { updateComicProject, isValidComicProjectPublicId } from '../../api/comics'
import { resolveReferenceImageDisplayUrl } from '../../api/referenceUpload'
import { AsyncPullLoadingIndicator } from '../../components/async/AsyncPullLoadingIndicator'
import { PageAsyncErrorState } from '../../components/async/PageAsyncErrorState'
import { ChromePrimaryButton, ChromeToolbarButton, ChromeToolbarDangerButton } from '../../components/ChromeActions'
import { ChromeTooltip } from '../../components/ChromeTooltip'
import { ComicCreativeSettingsModal } from '../../components/comic/ComicCreativeSettingsModal'
import { ComicEpisodeCanvasList } from '../../components/comic/ComicEpisodeCanvasList'
import { ComicEpisodeCanvasScrollShell } from '../../components/comic/ComicEpisodeCanvasScrollShell'
import { ComicEpisodeDirectoryModal } from '../../components/comic/ComicEpisodeDirectoryModal'
import { ComicEpisodeEditModal } from '../../components/comic/ComicEpisodeEditModal'
import { ComicEpisodeGeneratePagePickModal } from '../../components/comic/ComicEpisodeGeneratePagePickModal'
import { ComicEpisodeStrip } from '../../components/comic/ComicEpisodeStrip'
import { ComicPageCandidatesModal } from '../../components/comic/ComicPageCandidatesModal'
import { ComicPageEditImageModal } from '../../components/comic/ComicPageEditImageModal'
import { ComicPageStrip } from '../../components/comic/ComicPageStrip'
import { ComicPanelCandidatesModal } from '../../components/comic/ComicPanelCandidatesModal'
import { ComicPanelGenerateActions } from '../../components/comic/ComicPanelGenerateActions'
import { comicEditorSelectClass } from '../../components/comic/comicPanelTokens'
import { ComicProjectReaderModal } from '../../components/comic/ComicProjectReaderModal'
import { ComicRailAddButton, ComicRailDeleteButton, ComicRailDirectoryButton, ComicRailEditButton } from '../../components/comic/ComicRailSection'
import type { ComicSaveStatus } from '../../components/comic/ComicSaveStatusPill'
import { ComicStoryboardRail } from '../../components/comic/ComicStoryboardRail'
import { ComicWebtoonReaderModal } from '../../components/comic/ComicWebtoonReaderModal'
import { TimelinePullIndicator } from '../../components/create/TimelineRefreshSurfaces'
import { useImageLightbox } from '../../context/ImageLightboxContext'
import { ThemeModal } from '../../components/modal'
import { ThemeLayerCrossfade, ThemeReveal } from '../../components/motion/ThemeMotion'
import { IconPencil } from '../../components/nav-icons'
import {
  type ComicEpisodeQueueEditorBridge,
  useComicEpisodeGenerateQueue,
  useComicEpisodeQueueEditorBridge,
} from '../../context/ComicEpisodeGenerateQueueContext'
import { usePlatformConfig } from '../../context/PlatformConfigContext'
import { useToast } from '../../context/ToastContext'
import { useMediaQuery } from '../../hooks/useMediaQuery'
import { useScrollPullToRefresh } from '../../hooks/useScrollPullToRefresh'
import { USER_PERMISSION, useUserPerm } from '../../hooks/useUserPerm'
import { ContentSkeleton } from '../../layouts/ContentSkeleton'
import { comicHasReferenceCapableModels } from '../../lib/comicCharacterRefs'
import { type ComicProjectCreative, validateComicProjectCreative, warningsForCreativeChange } from '../../lib/comicCreativeBible'
import { buildEpisodeSnapshots, isEpisodeDirty } from '../../lib/comicEditorDirty'
import { writeStoredActiveEpisodeId } from '../../lib/comicEditorStorage'
import { getEpisodeSettings } from '../../lib/comicEpisodeSettings'
import { selectPageCandidate } from '../../lib/comicPageCandidates'
import {
  projectHasInFlightPageGeneration,
  reconcileProjectPageGenerations,
} from '../../lib/comicPageGenerationRecovery'
import { pageShowsGeneratingOnCanvas } from '../../lib/comicPageImageStatus'
import {
  type ComicPageLayoutTemplate,
  getPageLayoutOption,
} from '../../lib/comicPageLayout'
import { comicPageToLightboxItems, lightboxIndexForImageUrl } from '../../lib/comicPageLightbox'
import {
  resolveComicModelReferenceCap,
} from '../../lib/comicPageReferenceCap'
import { findPageContainingPanel, getEpisodePage, getPanelsOnPage } from '../../lib/comicPages'
import { removePanelCandidate, selectPanelCandidate } from '../../lib/comicPanelCandidates'
import { comicPanelToLightboxItems } from '../../lib/comicPanelLightbox'
import {
  addEpisodeToProject,
  addPageToEpisode,
  addPanelToProject,
  getActiveEpisode,
  mirrorProjectSettingsFromPrimary,
  movePanelsToPageInEpisode,
  movePanelToPageInEpisode,
  removeEpisodeFromProject,
  removePageFromEpisode,
  removePanelFromProject,
  editEpisodeInProject,
  reorderEpisodesInProject,
  reorderPanelInEpisode,
  setPageLayoutInEpisode,
  setProjectCreative,
  setProjectSettings,
  updatePageInEpisode,
  updatePanelInProject,
} from '../../lib/comicProjectMutations'
import {
  type ComicProjectSettings,
  getProjectPanelAspect,
  panelAspectTailwindClass,
  projectHasGeneratedImages,
  syncComicProjectSettingsToPanelAspect,
  validateComicProjectSettingsForPanelAspect,
  warningsForSettingsChange,
} from '../../lib/comicProjectSettings'
import type { ComicEpisode, ComicEpisodeEditPatch, ComicProject } from '../../lib/comicTypes'
import { COMIC_PROJECT_INIT_TEMPLATE_PUBLIC_ID } from '../../lib/workflow/workflowComicProjectInit'
import { useTheme } from '../../theme/ThemeContext'
import { useComicEditorProjectPersistence } from './useComicEditorProjectPersistence'
import { useComicExportActions } from './useComicExportActions'
import { useComicPageGeneration } from './useComicPageGeneration'

type MobileTab = 'storyboard' | 'canvas'

type PageEditTarget = {
  episodeId: string
  pageId: string
  initialCandidateId?: string | null
}

export function ComicEditorPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { projectId } = useParams<{ projectId: string }>()
  const { theme } = useTheme()
  const angular = theme === 'pixel' || theme === 'geek'
  const wideScreen = useMediaQuery('(min-width: 1024px)')
  const prefersReducedMotion = useReducedMotion()
  const motionPreset = useThemeMotion()
  const tabMotionPreset = mergeReducedMotionPreset(motionPreset, !!prefersReducedMotion)
  const tabIndicatorTransition = resolveTabIndicatorTransition(tabMotionPreset, {
    reducedMotion: !!prefersReducedMotion,
  })
  const { showToast } = useToast()
  const canUpdateProject = useUserPerm(USER_PERMISSION.comicProjectUpdate)
  const canEpisodeJobCreate = useUserPerm(USER_PERMISSION.comicEpisodeJobCreate)
  const {
    clientUi,
    remoteSynced: platformConfigRemoteSynced,
  } = usePlatformConfig()
  const platformConfigSynced = platformConfigRemoteSynced
  const comicModelsReady =
    platformConfigSynced &&
    comicHasReferenceCapableModels(clientUi) &&
    clientUi.imageSizeOptions.length > 0
  const [project, setProject] = useState<ComicProject | null>(null)
  const [creativeModalOpen, setCreativeModalOpen] = useState(false)
  const [episodeDirectoryModalOpen, setEpisodeDirectoryModalOpen] = useState(false)
  const [episodeEditOpen, setEpisodeEditOpen] = useState(false)
  const [candidatesPanelId, setCandidatesPanelId] = useState<string | null>(null)
  const [pageCandidatesPageId, setPageCandidatesPageId] = useState<string | null>(null)
  const [pageEditTarget, setPageEditTarget] = useState<PageEditTarget | null>(null)
  const [pageEditSubmitting, setPageEditSubmitting] = useState(false)
  const [workflowAttachOpen, setWorkflowAttachOpen] = useState(false)
  const [episodePagePickEpisodeId, setEpisodePagePickEpisodeId] = useState<string | null>(null)
  const pageSelectionWaitRef = useRef<Map<string, () => void>>(new Map())
  const {
    queue: episodeQueue,
    generatingEpisodeId,
    startEpisodeGenerate,
    openDetail: openEpisodeQueueDetail,
    clearAwaitingPage,
  } = useComicEpisodeGenerateQueue()
  const [webtoonReaderEpisodeId, setWebtoonReaderEpisodeId] = useState<string | null>(null)
  const [projectReaderOpen, setProjectReaderOpen] = useState(false)
  const [deletePanelId, setDeletePanelId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [editorRefreshing, setEditorRefreshing] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const storyboardScrollRef = useRef<HTMLDivElement | null>(null)
  const [storyboardPullAttachSignal, setStoryboardPullAttachSignal] = useState(0)
  const setStoryboardScrollPort = useCallback((node: HTMLDivElement | null) => {
    storyboardScrollRef.current = node
    setStoryboardPullAttachSignal((n) => n + 1)
  }, [])
  const [selectedEpisodeId, setSelectedEpisodeId] = useState<string | null>(null)
  const [selectedPanelId, setSelectedPanelId] = useState<string | null>(null)
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null)
  const [deleteEpisodeId, setDeleteEpisodeId] = useState<string | null>(null)
  const [deletePageId, setDeletePageId] = useState<string | null>(null)
  const [mobileTab, setMobileTab] = useState<MobileTab>('storyboard')
  const [generatingPageId, setGeneratingPageId] = useState<string | null>(null)
  // 灯箱上提到路由之上：切换页面时画布预览不丢
  const imageLightbox = useImageLightbox()
  const generatingPage = generatingPageId != null
  const pageGenerationInFlightRef = useRef<Set<string>>(new Set())
  type ExportConfirmState =
    | { kind: 'page'; pageId: string; label: string }
    | { kind: 'zip'; episodeId: string; label: string }
    | { kind: 'pdf'; episodeId: string; label: string }
    | null
  const [exportConfirm, setExportConfirm] = useState<ExportConfirmState>(null)
  const [draggingPanelId, setDraggingPanelId] = useState<string | null>(null)
  const [canvasEpisodeIds, setCanvasEpisodeIds] = useState<string[]>([])
  const [savedSnapshots, setSavedSnapshots] = useState<Map<string, string>>(() => new Map())
  const [saving, setSaving] = useState(false)
  const projectRef = useRef<ComicProject | null>(null)
  const selectedPanelIdRef = useRef<string | null>(null)
  const selectedPageIdRef = useRef<string | null>(null)
  const loadedForProjectIdRef = useRef<string | null>(null)
  const addingEpisodeRef = useRef(false)

  useEffect(() => {
    projectRef.current = project
  }, [project])

  useEffect(() => {
    selectedPanelIdRef.current = selectedPanelId
  }, [selectedPanelId])

  useEffect(() => {
    selectedPageIdRef.current = selectedPageId
  }, [selectedPageId])

  const selectFirstInEpisode = useCallback((ep: ComicEpisode) => {
    const firstPageId = ep.pages[0]?.id ?? null
    const firstPanelId = ep.panels[0]?.id ?? null
    selectedPageIdRef.current = firstPageId
    selectedPanelIdRef.current = firstPanelId
    setSelectedPageId(firstPageId)
    setSelectedPanelId(firstPanelId)
  }, [])

  const syncSelectionToEpisode = useCallback((ep: ComicEpisode, panelId: string | null) => {
    if (!panelId) return
    const page = findPageContainingPanel(ep, panelId)
    if (page) {
      selectedPageIdRef.current = page.id
      setSelectedPageId(page.id)
    }
  }, [])

  const selectPanel = useCallback(
    (panelId: string, ep?: ComicEpisode) => {
      selectedPanelIdRef.current = panelId
      setSelectedPanelId(panelId)
      const episodeForLookup =
        ep ??
        (projectRef.current
          ? getActiveEpisode(projectRef.current, selectedEpisodeId)
          : null)
      if (episodeForLookup) syncSelectionToEpisode(episodeForLookup, panelId)
    },
    [syncSelectionToEpisode, selectedEpisodeId],
  )

  const {
    applyProject,
    fetchProject,
    mergeComicProjectAfterSave,
    persistProject,
  } = useComicEditorProjectPersistence({
    clientUi,
    showToast,
    projectId,
    platformConfigSynced,
    comicModelsReady,
    selectedEpisodeId,
    selectedPanelIdRef,
    selectedPageIdRef,
    projectRef,
    loadedForProjectIdRef,
    setProject,
    setSaving,
    setSavedSnapshots,
    setLoading,
    setEditorRefreshing,
    setLoadError,
    setSelectedEpisodeId,
    setSelectedPageId,
    setCanvasEpisodeIds,
    selectPanel,
    selectFirstInEpisode,
  })

  const { generatePageForEpisode } = useComicPageGeneration({
    comicModelsReady,
    clientUi,
    applyProject,
    persistProject,
    showToast,
    pageGenerationInFlightRef,
  })

  useEffect(() => {
    loadedForProjectIdRef.current = null
  }, [projectId])

  useEffect(() => {
    if (!comicModelsReady || loading) return

    let cancelled = false
    let inFlight = false

    const tick = async () => {
      if (cancelled || inFlight) return
      const base = projectRef.current
      if (!base || !projectHasInFlightPageGeneration(base)) return
      inFlight = true
      try {
        const rec = await reconcileProjectPageGenerations(base, clientUi, {
          skipPageKeys: pageGenerationInFlightRef.current,
          waitForInFlight: true,
        })
        if (cancelled || !rec.changed) return
        let next = rec.project
        try {
          const saved = await updateComicProject(next, clientUi)
          if (!cancelled) next = mergeComicProjectAfterSave(next, saved)
        } catch {
          /* 保留本地恢复结果 */
        }
        if (cancelled) return
        setProject(next)
        projectRef.current = next
        setSavedSnapshots(buildEpisodeSnapshots(next))
        const msg = rec.messages[0]
        if (msg) {
          showToast({ message: msg, variant: 'success', autoCloseMs: 4000 })
        }
      } finally {
        inFlight = false
      }
    }

    void tick()
    const id = window.setInterval(() => void tick(), 4000)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [comicModelsReady, loading, clientUi, mergeComicProjectAfterSave, showToast])

  useEffect(() => {
    if (!projectId || !platformConfigSynced) return
    if (!comicModelsReady) {
      setLoading(false)
      setEditorRefreshing(false)
      setLoadError(null)
      setProject(null)
      return
    }
    if (loadedForProjectIdRef.current === projectId && projectRef.current?.id === projectId) {
      return
    }
    let cancelled = false
    void fetchProject({ initial: true, isCancelled: () => cancelled })
    return () => {
      cancelled = true
    }
  }, [projectId, platformConfigSynced, comicModelsReady, fetchProject])

  const showInitialLoading = loading && !project

  const onEditorPullRefreshCommit = useCallback(() => {
    void fetchProject({ initial: false })
  }, [fetchProject])

  const {
    pullPx: storyboardPullPx,
    commitFlying: storyboardPullCommitFlying,
    onCommitFlyComplete: onStoryboardPullCommitFlyComplete,
    releaseThreshold: storyboardPullReleaseThreshold,
  } = useScrollPullToRefresh({
    scrollRef: storyboardScrollRef,
    attachSignal: storyboardPullAttachSignal,
    enabled: !showInitialLoading && !!project && (wideScreen || mobileTab === 'storyboard'),
    refreshing: editorRefreshing,
    onCommit: onEditorPullRefreshCommit,
  })

  const episode = project ? getActiveEpisode(project, selectedEpisodeId) : null

  const openWorkflowApply = useCallback(
    (applyMode: 'append_episodes' | 'replace_selected_episode' | 'fill_empty_pages') => {
      if (!project || !isValidComicProjectPublicId(project.id)) {
        showToast({ message: '漫画项目标识无效', variant: 'warning' })
        return
      }
      const params = new URLSearchParams({
        mode: 'run',
        template: COMIC_PROJECT_INIT_TEMPLATE_PUBLIC_ID,
        applyProjectId: project.id,
        applyMode,
      })
      if (applyMode !== 'append_episodes' && selectedEpisodeId) {
        params.set('targetEpisodeId', selectedEpisodeId)
      }
      setWorkflowAttachOpen(false)
      navigate(`/workflows?${params.toString()}`)
    },
    [navigate, project, selectedEpisodeId, showToast],
  )
  const {
    exportingPageId,
    exportingEpisodeId,
    exportingEpisodePdfId,
    handleExportPage,
    handleExportEpisode,
    handleExportEpisodePdf,
  } = useComicExportActions({ project, episode, showToast })

  const handleSelectEpisode = useCallback(
    (episodeId: string) => {
      if (!project) return
      setSelectedEpisodeId(episodeId)
      if (projectId) writeStoredActiveEpisodeId(projectId, episodeId)
      const ep = getActiveEpisode(project, episodeId)
      selectFirstInEpisode(ep)
      setCanvasEpisodeIds((prev) => (prev.includes(episodeId) ? prev : [...prev, episodeId]))
      if (!wideScreen) setMobileTab('storyboard')
    },
    [project, projectId, selectFirstInEpisode, wideScreen],
  )

  const handleToggleCanvasEpisode = useCallback(
    (episodeId: string, checked: boolean) => {
      setCanvasEpisodeIds((prev) => {
        if (checked) {
          return prev.includes(episodeId) ? prev : [...prev, episodeId]
        }
        return prev.filter((id) => id !== episodeId)
      })
    },
    [],
  )

  const handleEditEpisode = useCallback(
    (episodeId: string, patch: ComicEpisodeEditPatch) => {
      if (!project) return
      applyProject(editEpisodeInProject(project, episodeId, patch))
    },
    [project, applyProject],
  )

  const handleReorderEpisode = useCallback(
    (sourceEpisodeId: string, targetEpisodeId: string) => {
      if (!project || generatingEpisodeId || episodeQueue) {
        showToast({ message: '生成本话进行中，暂不能调整话次顺序', variant: 'warning' })
        return
      }
      const next = reorderEpisodesInProject(project, sourceEpisodeId, targetEpisodeId)
      if (next === project) return
      applyProject(next, { save: true })
      showToast({ message: '已调整话次顺序', variant: 'success', autoCloseMs: 2200 })
    },
    [project, generatingEpisodeId, episodeQueue, applyProject, showToast],
  )

  const episodeSaveStatus = useCallback(
    (episodeId: string): ComicSaveStatus => {
      if (!project) return 'saved'
      const ep = project.episodes.find((e) => e.id === episodeId)
      if (!ep) return 'saved'
      if (saving && isEpisodeDirty(ep, savedSnapshots)) return 'saving'
      return isEpisodeDirty(ep, savedSnapshots) ? 'dirty' : 'saved'
    },
    [project, savedSnapshots, saving],
  )

  const scrollCanvasToPage = useCallback((pageId: string) => {
    requestAnimationFrame(() => {
      document.getElementById(`comic-canvas-page-${pageId}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    })
  }, [])

  const handleAddEpisode = useCallback(() => {
    const base = projectRef.current
    if (!base || addingEpisodeRef.current) return
    addingEpisodeRef.current = true
    const { project: next, episodeId } = addEpisodeToProject(base, clientUi, {
      copySettingsFromEpisodeId: selectedEpisodeId,
    })
    applyProject(next, { save: true })
    handleSelectEpisode(episodeId)
    showToast({ message: '已新建一话', variant: 'success', autoCloseMs: 2200 })
    window.setTimeout(() => {
      addingEpisodeRef.current = false
    }, 400)
  }, [clientUi, selectedEpisodeId, applyProject, handleSelectEpisode, showToast])

  const confirmDeleteEpisode = useCallback(() => {
    const targetId = deleteEpisodeId
    if (!targetId || !project) return
    const removed = removeEpisodeFromProject(project, targetId)
    if (!removed) {
      showToast({ message: '至少保留一话', variant: 'warning' })
      setDeleteEpisodeId(null)
      return
    }
    const next = mirrorProjectSettingsFromPrimary(removed, clientUi)
    const fallbackId = next.episodes[0]?.id ?? null
    applyProject(next, { save: true })
    if (fallbackId) handleSelectEpisode(fallbackId)
    setDeleteEpisodeId(null)
    showToast({ message: '已删除该话', variant: 'success', autoCloseMs: 2200 })
  }, [
    deleteEpisodeId,
    project,
    clientUi,
    applyProject,
    handleSelectEpisode,
    showToast,
  ])
  const currentPage = episode ? getEpisodePage(episode, selectedPageId) : null
  const pagePanels = episode && currentPage ? getPanelsOnPage(episode, currentPage) : []
  const pageReferenceCap = useMemo(() => {
    if (!project || !episode || !currentPage) return null
    const panelsOnPage = getPanelsOnPage(episode, currentPage)
    const s = getEpisodeSettings(project, episode, clientUi)
    return {
      creative: project.creative,
      pagePanels: panelsOnPage,
      cap: resolveComicModelReferenceCap(clientUi, s.platformId, s.modelId),
    }
  }, [project, episode, currentPage, clientUi])
  const requestDeletePage = useCallback((pageId: string) => {
    setDeletePageId(pageId)
  }, [])

  const confirmDeletePage = useCallback(() => {
    const pageId = deletePageId
    if (!pageId || !project || !episode) return
    const removed = removePageFromEpisode(project, episode.id, pageId)
    if (!removed) {
      showToast({
        message: '至少保留一页',
        variant: 'warning',
      })
      setDeletePageId(null)
      return
    }
    applyProject(removed.project, { save: true })
    selectedPageIdRef.current = removed.nextPageId
    setSelectedPageId(removed.nextPageId)
    const ep = getActiveEpisode(removed.project, selectedEpisodeId)
    const nextPage = ep.pages.find((p) => p.id === removed.nextPageId) ?? ep.pages[0]
    const firstPanelId = nextPage?.panelIds[0] ?? ep.panels[0]?.id ?? null
    if (firstPanelId) selectPanel(firstPanelId, ep)
    else setSelectedPanelId(null)
    if (removed.nextPageId) scrollCanvasToPage(removed.nextPageId)
    setDeletePageId(null)
    showToast({ message: '已删除该页', variant: 'success', autoCloseMs: 2200 })
  }, [
    deletePageId,
    project,
    episode,
    selectedEpisodeId,
    applyProject,
    selectPanel,
    scrollCanvasToPage,
    showToast,
  ])

  const handleSelectPage = useCallback(
    (pageId: string) => {
      selectedPageIdRef.current = pageId
      setSelectedPageId(pageId)
      const base = projectRef.current
      if (!base) return
      const ep = getActiveEpisode(base, selectedEpisodeId)
      const page = ep.pages.find((p) => p.id === pageId)
      const first = page ? getPanelsOnPage(ep, page)[0] : null
      if (first) selectPanel(first.id, ep)
      else {
        selectedPanelIdRef.current = null
        setSelectedPanelId(null)
      }
    },
    [selectedEpisodeId, selectPanel],
  )

  const handleAddPage = useCallback(() => {
    if (!project || !episode) return
    const anchorPage =
      (selectedPageId && episode.pages.find((p) => p.id === selectedPageId)) ??
      episode.pages.at(-1)
    const layout = anchorPage && typeof anchorPage === 'object' ? anchorPage.layout : undefined
    const { project: next, pageId } = addPageToEpisode(project, episode.id, layout)
    applyProject(next)
    handleSelectPage(pageId)
    scrollCanvasToPage(pageId)
    showToast({ message: '已新建一页', variant: 'success', autoCloseMs: 2200 })
  }, [project, episode, selectedPageId, applyProject, handleSelectPage, scrollCanvasToPage, showToast])

  const handlePageLayoutChange = useCallback(
    (layout: ComicPageLayoutTemplate) => {
      if (!project || !episode || !currentPage) return
      let next = setPageLayoutInEpisode(project, episode.id, currentPage.id, layout)
      next = updatePageInEpisode(next, episode.id, currentPage.id, {
        pageImageUrl: null,
        selectedPageCandidateId: null,
        pageCandidates: [],
        pageImageStatus: 'draft',
        pageErrorMessage: undefined,
        pageCreationTaskId: null,
      })
      applyProject(next)
    },
    [project, episode, currentPage, applyProject],
  )

  const handleSelectPanelCandidate = useCallback(
    (candidateId: string, panelId?: string) => {
      const pid = panelId ?? selectedPanelId
      if (!project || !episode || !pid) return
      applyProject(
        selectPanelCandidate(project, episode.id, pid, candidateId),
        { save: true },
      )
      showToast({ message: '已定为当前格主图', variant: 'success', autoCloseMs: 2200 })
    },
    [project, episode, selectedPanelId, applyProject, showToast],
  )

  const resumePageSelection = useCallback((pageId: string) => {
    pageSelectionWaitRef.current.get(pageId)?.()
    pageSelectionWaitRef.current.delete(pageId)
  }, [])

  const waitForPageSelection = useCallback((pageId: string) => {
    return new Promise<void>((resolve) => {
      pageSelectionWaitRef.current.set(pageId, resolve)
    })
  }, [])

  const handleApplyPageCandidate = useCallback(
    (candidateId: string, pageId?: string) => {
      const pid = pageId ?? pageCandidatesPageId ?? selectedPageId
      if (!project || !episode || !pid) return
      const page = getEpisodePage(episode, pid)
      if (!page) return
      const next = selectPageCandidate(project, episode.id, pid, candidateId)
      applyProject(next, { save: true })
      setPageCandidatesPageId(null)
      clearAwaitingPage()
      resumePageSelection(pid)
      showToast({ message: '已应用为本页整页图', variant: 'success', autoCloseMs: 2200 })
    },
    [project, episode, pageCandidatesPageId, selectedPageId, applyProject, clearAwaitingPage, resumePageSelection, showToast],
  )

  const handleRequestEditPageImage = useCallback(
    (pageId: string, initialCandidateId?: string | null) => {
      if (!project) return
      const targetEpisode = project.episodes.find((ep) => ep.pages.some((p) => p.id === pageId))
      const page = targetEpisode ? getEpisodePage(targetEpisode, pageId) : null
      if (!targetEpisode || !page) return
      const hasBaseImage =
        page.pageImageUrl?.trim() ||
        page.pageCandidates.some((c) => c.imageUrl?.trim())
      if (!hasBaseImage) {
        showToast({ message: '请先生成或选择一张整页图后再修字', variant: 'warning' })
        return
      }
      if (pageShowsGeneratingOnCanvas(page) || generatingPageId === pageId) {
        showToast({ message: '当前分页正在生成，请稍后再试', variant: 'warning' })
        return
      }
      if (targetEpisode.id !== selectedEpisodeId) {
        handleSelectEpisode(targetEpisode.id)
      }
      handleSelectPage(pageId)
      setPageCandidatesPageId(null)
      setPageEditTarget({
        episodeId: targetEpisode.id,
        pageId,
        initialCandidateId: initialCandidateId ?? null,
      })
    },
    [project, generatingPageId, selectedEpisodeId, handleSelectEpisode, handleSelectPage, showToast],
  )

  const handleConfirmPageEditImage = useCallback(
    async (payload: {
      mode: ComicPageRegenerateMode
      editPrompt: string
      candidateId: string | null
    }) => {
      if (!project || !pageEditTarget || pageEditSubmitting) return
      setPageEditSubmitting(true)
      setGeneratingPageId(pageEditTarget.pageId)
      try {
        const result = await generatePageForEpisode(
          project,
          pageEditTarget.episodeId,
          pageEditTarget.pageId,
          undefined,
          {
            generateRequest: {
              regenerateMode: payload.mode,
              editPrompt: payload.editPrompt,
              baseCandidateId: payload.candidateId,
              referencePageImage: payload.candidateId == null,
            },
            successMessage: '修正版已生成，请在候选中选择应用',
          },
        )
        setPageEditTarget(null)
        if (result.needsSelection || payload.mode === 'edit_text' || payload.mode === 'edit_image') {
          setPageCandidatesPageId(pageEditTarget.pageId)
        }
        scrollCanvasToPage(pageEditTarget.pageId)
      } finally {
        setGeneratingPageId(null)
        setPageEditSubmitting(false)
      }
    },
    [project, pageEditTarget, pageEditSubmitting, generatePageForEpisode, scrollCanvasToPage],
  )

  const handleGeneratePage = useCallback(
    async (settingsOverride?: ComicProjectSettings) => {
      if (!project || !episode || !currentPage || generatingPage || generatingEpisodeId) return
      setGeneratingPageId(currentPage.id)
      try {
        const result = await generatePageForEpisode(
          project,
          episode.id,
          currentPage.id,
          settingsOverride,
        )
        if (result.needsSelection) {
          setPageCandidatesPageId(currentPage.id)
          scrollCanvasToPage(currentPage.id)
        }
      } finally {
        setGeneratingPageId(null)
      }
    },
    [
      project,
      episode,
      currentPage,
      generatingPage,
      generatingEpisodeId,
      generatePageForEpisode,
      scrollCanvasToPage,
    ],
  )

  const editorBridge = useMemo((): ComicEpisodeQueueEditorBridge | null => {
    if (!project) return null
    return {
      projectId: project.id,
      applyProject,
      persistProject,
      waitForPageSelection,
      openPageCandidates: (pageId) => setPageCandidatesPageId(pageId),
      scrollToPage: scrollCanvasToPage,
    }
  }, [project, applyProject, persistProject, waitForPageSelection, scrollCanvasToPage])

  useComicEpisodeQueueEditorBridge(editorBridge)

  const handleRequestGenerateEpisode = useCallback(
    (episodeId: string) => {
      if (!canEpisodeJobCreate) {
        showToast({ message: '没有话级生成权限', variant: 'warning' })
        return
      }
      if (!project || generatingEpisodeId || generatingPage) return
      setEpisodePagePickEpisodeId(episodeId)
    },
    [canEpisodeJobCreate, project, generatingEpisodeId, generatingPage, showToast],
  )

  const handleConfirmEpisodePagePick = useCallback(
    async (pageIds: string[]) => {
      if (!project || !episodePagePickEpisodeId) return
      const episodeId = episodePagePickEpisodeId
      setEpisodePagePickEpisodeId(null)
      await startEpisodeGenerate(project, episodeId, { pageIds })
    },
    [project, episodePagePickEpisodeId, startEpisodeGenerate],
  )

  useEffect(() => {
    const st = location.state as {
      episodeId?: string
      pageCandidatesPageId?: string
      scrollToPageId?: string
      openEpisodeQueueDetail?: boolean
    } | null
    if (!st || !project) return
    if (st.episodeId) {
      setSelectedEpisodeId(st.episodeId)
      writeStoredActiveEpisodeId(project.id, st.episodeId)
    }
    if (st.pageCandidatesPageId) {
      setPageCandidatesPageId(st.pageCandidatesPageId)
      scrollCanvasToPage(st.pageCandidatesPageId)
    }
    if (st.scrollToPageId) {
      scrollCanvasToPage(st.scrollToPageId)
    }
    if (st.openEpisodeQueueDetail) openEpisodeQueueDetail()
    navigate(location.pathname, { replace: true, state: null })
  }, [
    location.pathname,
    location.state,
    project,
    navigate,
    scrollCanvasToPage,
    openEpisodeQueueDetail,
  ])

  const handleAddPanel = useCallback(() => {
    if (!project || !episode) return
    const { project: next, panel } = addPanelToProject(project, episode.id, {
      pageId: selectedPageId,
    })
    applyProject(next)
    const ep = getActiveEpisode(next, selectedEpisodeId)
    selectPanel(panel.id, ep)
    const pageId = findPageContainingPanel(ep, panel.id)?.id ?? selectedPageId
    if (pageId) scrollCanvasToPage(pageId)
    if (!wideScreen) setMobileTab('storyboard')
    showToast({ message: `已添加分镜 #${panel.order}`, variant: 'success', autoCloseMs: 2800 })
  }, [
    project,
    episode,
    selectedPageId,
    selectedEpisodeId,
    applyProject,
    wideScreen,
    showToast,
    selectPanel,
    scrollCanvasToPage,
  ])

  const handleSaveCreativeSettings = useCallback(
    (creative: ComicProjectCreative, settings: ComicProjectSettings | null) => {
      const base = projectRef.current
      if (!base) return
      const creativeValidation = validateComicProjectCreative(creative)
      if (!creativeValidation.ok) {
        showToast({ message: creativeValidation.message, variant: 'warning' })
        return
      }
      const creativeWarnings = warningsForCreativeChange(
        base.creative,
        creative,
        projectHasGeneratedImages(base),
      )
      for (const w of creativeWarnings) {
        showToast({ message: w.message, variant: 'warning', autoCloseMs: 6000 })
      }
      let next = setProjectCreative(base, creative, clientUi)
      if (settings) {
        const syncedSettings = syncComicProjectSettingsToPanelAspect(
          settings,
          clientUi,
          creative.panelAspect,
        )
        const settingsValidation = validateComicProjectSettingsForPanelAspect(
          syncedSettings,
          clientUi,
          creative.panelAspect,
        )
        if (!settingsValidation.ok) {
          showToast({ message: settingsValidation.message, variant: 'warning' })
          return
        }
        const settingsWarnings = warningsForSettingsChange(
          base.settings,
          syncedSettings,
          projectHasGeneratedImages(base),
        )
        for (const w of settingsWarnings) {
          showToast({ message: w.message, variant: 'warning', autoCloseMs: 6000 })
        }
        next = setProjectSettings(next, syncedSettings)
      }
      applyProject(next, { save: true })
      setCreativeModalOpen(false)
      showToast({ message: '画册设置已保存', variant: 'success', autoCloseMs: 2200 })
    },
    [applyProject, showToast, clientUi],
  )

  const handleMovePanel = useCallback(
    (panelId: string, direction: 'up' | 'down') => {
      if (!project || !episode || !selectedPageId) return
      applyProject(
        reorderPanelInEpisode(project, episode.id, selectedPageId, panelId, direction),
      )
    },
    [project, episode, selectedPageId, applyProject],
  )

  const applyPanelMove = useCallback(
    (panelId: string, targetPageId: string, insertIndex: number) => {
      if (!project || !episode) return
      const next = movePanelToPageInEpisode(
        project,
        episode.id,
        panelId,
        targetPageId,
        insertIndex,
      )
      if (!next) {
        const page = episode.pages.find((p) => p.id === targetPageId)
        const max = page ? getPageLayoutOption(page.layout).maxPanels : 0
        showToast({
          message: `目标页已满（最多 ${max} 格）`,
          variant: 'warning',
        })
        return
      }
      applyProject(next)
      const ep = getActiveEpisode(next, selectedEpisodeId)
      const fromPage = episode ? findPageContainingPanel(episode, panelId) : null
      const targetPage = ep.pages.find((p) => p.id === targetPageId)
      setSelectedPageId(targetPageId)
      selectPanel(panelId, ep)
      if (fromPage && targetPage && fromPage.id !== targetPageId) {
        showToast({
          message: `已移动到 ${targetPage.title}`,
          variant: 'success',
          autoCloseMs: 2800,
        })
      }
    },
    [project, episode, selectedEpisodeId, applyProject, showToast, selectPanel],
  )

  const applyPanelsMove = useCallback(
    (panelIds: string[], targetPageId: string) => {
      if (!project || !episode || panelIds.length === 0) return
      const next = movePanelsToPageInEpisode(project, episode.id, panelIds, targetPageId)
      if (!next) {
        const page = episode.pages.find((p) => p.id === targetPageId)
        const max = page ? getPageLayoutOption(page.layout).maxPanels : 0
        showToast({
          message: `目标页已满（最多 ${max} 格）`,
          variant: 'warning',
        })
        return
      }
      applyProject(next)
      const ep = getActiveEpisode(next, selectedEpisodeId)
      const targetPage = ep.pages.find((p) => p.id === targetPageId)
      setSelectedPageId(targetPageId)
      const keep = panelIds.find((id) => ep.panels.some((p) => p.id === id))
      if (keep) selectPanel(keep, ep)
      else setSelectedPanelId(null)
      showToast({
        message: `已迁移 ${panelIds.length} 格到 ${targetPage?.title ?? '目标页'}`,
        variant: 'success',
        autoCloseMs: 2800,
      })
    },
    [project, episode, selectedEpisodeId, applyProject, showToast, selectPanel],
  )

  const handlePanelDropOnPage = useCallback(
    (targetPageId: string, panelId: string) => {
      const page = episode?.pages.find((p) => p.id === targetPageId)
      const insertIndex = page
        ? page.panelIds.filter((id) => id !== panelId).length
        : 0
      applyPanelMove(panelId, targetPageId, insertIndex)
    },
    [episode, applyPanelMove],
  )

  const handlePanelDropOnSlot = useCallback(
    (panelId: string, insertIndex: number) => {
      if (!selectedPageId) return
      applyPanelMove(panelId, selectedPageId, insertIndex)
    },
    [selectedPageId, applyPanelMove],
  )

  const candidatesPanel = episode?.panels.find((p) => p.id === candidatesPanelId) ?? null

  const pageCandidatesPage =
    pageCandidatesPageId && episode
      ? (getEpisodePage(episode, pageCandidatesPageId) ?? null)
      : null

  const pageEditPage =
    pageEditTarget && project
      ? (project.episodes
          .find((ep) => ep.id === pageEditTarget.episodeId)
          ?.pages.find((p) => p.id === pageEditTarget.pageId) ?? null)
      : null

  const handleOpenCanvasImagePreview = useCallback(
    (payload: {
      pageId: string
      imageUrl: string
      panelId: string | null
      scope: 'page' | 'panel'
    }) => {
      if (!episode) return
      const page = getEpisodePage(episode, payload.pageId)
      if (!page) return
      const panel = payload.panelId
        ? (episode.panels.find((p) => p.id === payload.panelId) ?? null)
        : null
      const items =
        payload.scope === 'page'
          ? comicPageToLightboxItems(page)
          : comicPanelToLightboxItems(panel)
      if (items.length === 0) {
        const src = resolveReferenceImageDisplayUrl(payload.imageUrl.trim())
        if (!src) return
        imageLightbox.open([{ src, subtitle: page.title, alt: page.title }], { index: 0 })
        return
      }
      imageLightbox.open(items, { index: lightboxIndexForImageUrl(items, payload.imageUrl) })
    },
    [episode, imageLightbox],
  )

  const requestRemovePanel = useCallback((panelId: string) => {
    setDeletePanelId(panelId)
  }, [])

  const confirmRemovePanel = useCallback(() => {
    const panelId = deletePanelId
    if (!panelId || !project || !episode) return
    const next = removePanelFromProject(project, episode.id, panelId)
    applyProject(next)
    const ep = getActiveEpisode(next, selectedEpisodeId)
    setSelectedPageId((cur) => {
      if (cur && ep.pages.some((p) => p.id === cur)) return cur
      return ep.pages[0]?.id ?? null
    })
    setSelectedPanelId((cur) => {
      if (cur !== panelId) return cur
      return ep.panels[0]?.id ?? null
    })
    const pageId = selectedPageIdRef.current ?? ep.pages[0]?.id ?? null
    if (pageId) scrollCanvasToPage(pageId)
    setDeletePanelId(null)
  }, [deletePanelId, project, episode, selectedEpisodeId, applyProject, scrollCanvasToPage])

  const deletePanelTarget = deletePanelId
    ? episode?.panels.find((p) => p.id === deletePanelId) ?? null
    : null
  const deletePanelLabel = deletePanelTarget
    ? `分镜 ${deletePanelTarget.order + 1}${
        deletePanelTarget.script.trim()
          ? `：${deletePanelTarget.script.trim().slice(0, 40)}${deletePanelTarget.script.trim().length > 40 ? '…' : ''}`
          : ''
      }`
    : ''

  if (!projectId || !isValidComicProjectPublicId(projectId)) {
    return <Navigate to="/comics" replace />
  }

  if (!loading && loadError) {
    return (
      <ContentSkeleton
        title="漫画"
        breadcrumbs={[
          { label: '首页', to: '/' },
          { label: '漫画', to: '/comics' },
          { label: '加载失败' },
        ]}
        adminLayout
        framed={false}
      >
        <div className="flex min-h-0 flex-1 flex-col">
          <PageAsyncErrorState
            theme={theme}
            title="漫画详情加载失败"
            detail={loadError}
            retryLabel="重试加载"
            onRetry={() => void fetchProject({ initial: true })}
          />
        </div>
      </ContentSkeleton>
    )
  }

  if (!loading && platformConfigSynced && !comicModelsReady) {
    return (
      <ContentSkeleton
        title="漫画"
        breadcrumbs={[
          { label: '首页', to: '/' },
          { label: '漫画', to: '/comics' },
          { label: '无法编辑' },
        ]}
        adminLayout
        framed={false}
      >
        <div className="flex min-h-0 flex-1 flex-col">
          <PageAsyncErrorState
            theme={theme}
            title="无法编辑此漫画"
            detail="当前没有支持参考图的平台或模型，漫画生图需要图生图能力。请在后台启用对应平台（如诗云 GPT Image 2）后刷新页面。"
            retryLabel="返回书架"
            onRetry={() => navigate('/comics')}
          />
        </div>
      </ContentSkeleton>
    )
  }

  if (!loading && !project) {
    return (
      <ContentSkeleton
        title="漫画"
        breadcrumbs={[
          { label: '首页', to: '/' },
          { label: '漫画', to: '/comics' },
          { label: '未找到项目' },
        ]}
        adminLayout
        framed={false}
      >
        <div className="flex min-h-0 flex-1 flex-col">
          <PageAsyncErrorState
            theme={theme}
            title="未找到漫画项目"
            detail="该项目可能已删除，或当前账号无访问权限。"
            retryLabel="返回书架"
            onRetry={() => navigate('/comics')}
          />
        </div>
      </ContentSkeleton>
    )
  }

  const storyboardColumn = project && episode ? (
    <ThemeReveal className="flex min-h-0 min-w-0 flex-col">
      <ComicEpisodeStrip
        angular={angular}
        episodes={project.episodes}
        selectedEpisodeId={selectedEpisodeId}
        onSelectEpisode={handleSelectEpisode}
        onAddEpisode={canUpdateProject ? handleAddEpisode : undefined}
        onDeleteEpisode={canUpdateProject ? (id) => setDeleteEpisodeId(id) : undefined}
        onEditEpisode={canUpdateProject ? handleEditEpisode : undefined}
        onReorderEpisode={
          canUpdateProject && !generatingEpisodeId && !episodeQueue ? handleReorderEpisode : undefined
        }
        canvasEpisodeIds={canvasEpisodeIds}
        episodeSaveStatus={episodeSaveStatus}
        onToggleCanvasEpisode={handleToggleCanvasEpisode}
        wideScreen={wideScreen}
      />
      <ComicPageStrip
        angular={angular}
        pages={episode.pages}
        selectedPageId={selectedPageId}
        onSelectPage={handleSelectPage}
        onAddPage={canUpdateProject && !wideScreen ? handleAddPage : undefined}
        onDeletePage={canUpdateProject ? requestDeletePage : undefined}
        onLayoutChange={canUpdateProject ? handlePageLayoutChange : () => {}}
        draggingPanelId={draggingPanelId}
        onPanelDropOnPage={handlePanelDropOnPage}
        onOpenPageCandidates={(pageId) => {
          setPageCandidatesPageId(pageId)
        }}
      />
      <ComicStoryboardRail
        angular={angular}
        characters={project.creative.characters}
        episode={episode}
        referenceCap={pageReferenceCap}
        selectedPanelId={selectedPanelId}
        selectedPageId={selectedPageId}
        onSelectPanel={(id) => selectPanel(id, episode ?? undefined)}
        onAddPanel={canUpdateProject ? handleAddPanel : undefined}
        onRemovePanel={requestRemovePanel}
        onMovePanel={handleMovePanel}
        canEdit={canUpdateProject}
        draggingPanelId={draggingPanelId}
        onDragStartPanel={setDraggingPanelId}
        onDragEndPanel={() => setDraggingPanelId(null)}
        onPanelDropOnSlot={handlePanelDropOnSlot}
        onMovePanelToPage={(panelId, targetPageId) => {
          const page = episode.pages.find((p) => p.id === targetPageId)
          const insertIndex = page
            ? page.panelIds.filter((id) => id !== panelId).length
            : 0
          applyPanelMove(panelId, targetPageId, insertIndex)
        }}
        onMovePanelsToPage={(panelIds, targetPageId) => applyPanelsMove(panelIds, targetPageId)}
        panelSaveBusy={saving}
        onSavePanel={(panelId, patch) => {
          if (!canUpdateProject) return
          applyProject(
            updatePanelInProject(project, episode.id, panelId, {
              script: patch.script,
              dialogue: patch.dialogue,
              narration: patch.narration,
              referenceImageUrls: patch.referenceImageUrls,
              characterIds: patch.characterIds,
              characterLines: patch.characterLines,
            }),
            { save: true },
          )
          showToast({ message: '分镜已保存', variant: 'success', autoCloseMs: 2200 })
        }}
        onSelectPanelCandidate={(candidateId) => handleSelectPanelCandidate(candidateId)}
      />
    </ThemeReveal>
  ) : null

  const storyboardGenerateBar =
    project && episode ? (
      <ComicPanelGenerateActions
        angular={angular}
        page={currentPage}
        pagePanels={pagePanels}
        generatingPage={generatingPage}
        platformReady={comicModelsReady}
        canGenerate={canEpisodeJobCreate}
        onGeneratePage={() => void handleGeneratePage()}
      />
    ) : null

  const canvasColumn =
    project && episode ? (
      <ComicEpisodeCanvasList
        angular={angular}
        episodes={project.episodes}
        canvasEpisodeIds={canvasEpisodeIds}
        activeEpisodeId={selectedEpisodeId ?? episode.id}
        panelAspect={getProjectPanelAspect(project)}
        project={project}
        clientUi={clientUi}
        selectedPanelId={selectedPanelId}
        selectedPageId={selectedPageId}
        generatingEpisodeId={generatingEpisodeId}
        generatingPageId={generatingPageId}
        platformReady={comicModelsReady}
        onGenerateEpisode={canEpisodeJobCreate ? handleRequestGenerateEpisode : undefined}
        onExportEpisode={(id) => {
          if (!project) return
          const ep = getActiveEpisode(project, id)
          setExportConfirm({ kind: 'zip', episodeId: id, label: ep.title })
        }}
        onExportEpisodePdf={(id) => {
          if (!project) return
          const ep = getActiveEpisode(project, id)
          setExportConfirm({ kind: 'pdf', episodeId: id, label: ep.title })
        }}
        exportingEpisodeId={exportingEpisodeId}
        exportingEpisodePdfId={exportingEpisodePdfId}
        onExportPage={(pageId) => {
          if (!project || !episode) return
          const page = getEpisodePage(episode, pageId)
          setExportConfirm({
            kind: 'page',
            pageId,
            label: page?.title ?? '该页',
          })
        }}
        exportingPageId={exportingPageId}
        onSelectPage={(pageId) => {
          const pageEp = project.episodes.find((e) => e.pages.some((p) => p.id === pageId))
          if (pageEp && pageEp.id !== selectedEpisodeId) {
            handleSelectEpisode(pageEp.id)
          }
          handleSelectPage(pageId)
        }}
        onSelectPanel={(panelId) => {
          const panelEp = project.episodes.find((e) => e.panels.some((p) => p.id === panelId))
          if (panelEp) selectPanel(panelId, panelEp)
        }}
        onOpenCandidates={(panelId) => {
          setCandidatesPanelId(panelId)
        }}
        onSelectPanelCandidate={(panelId, candidateId) =>
          handleSelectPanelCandidate(candidateId, panelId)
        }
        onOpenWebtoonReader={(episodeId) => setWebtoonReaderEpisodeId(episodeId)}
        onOpenPageCandidates={(pageId) => {
          const pageEp = project.episodes.find((e) => e.pages.some((p) => p.id === pageId))
          if (pageEp && pageEp.id !== selectedEpisodeId) {
            handleSelectEpisode(pageEp.id)
          }
          setPageCandidatesPageId(pageId)
        }}
        onEditPageImage={(pageId) => handleRequestEditPageImage(pageId)}
        onOpenImagePreview={handleOpenCanvasImagePreview}
      />
    ) : null

  const webtoonReaderEpisode =
    project && webtoonReaderEpisodeId
      ? project.episodes.find((e) => e.id === webtoonReaderEpisodeId) ?? null
      : null

  return (
    <ContentSkeleton
      title={project?.title ?? '漫画'}
      breadcrumbs={[
        { label: '首页', to: '/' },
        { label: '漫画', to: '/comics' },
        { label: project?.title ?? '…', title: project?.title },
      ]}
      headerTrailing={
        <div className="flex flex-wrap items-center justify-end gap-2">
          {project ? (
            <>
              <ChromeToolbarButton type="button" className="h-9" onClick={() => setProjectReaderOpen(true)}>
                预览
              </ChromeToolbarButton>
              {canUpdateProject ? (
                <ChromeToolbarButton type="button" className="h-9" onClick={() => setWorkflowAttachOpen(true)}>
                  接入工作流
                </ChromeToolbarButton>
              ) : null}
              {canUpdateProject ? (
                <ChromeToolbarButton type="button" className="h-9" onClick={() => setCreativeModalOpen(true)}>
                  画册设置
                </ChromeToolbarButton>
              ) : null}
            </>
          ) : null}
        </div>
      }
      adminLayout
      framed={false}
      scrollableBody={false}
      shellScrollable={false}
    >
      {showInitialLoading ? (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <div className="scroll-region min-h-0 flex-1 overflow-y-auto px-4 py-3 sm:px-6 sm:py-4">
            <AsyncPullLoadingIndicator angular={angular} label="正在加载作品…" className="pb-4" />
          </div>
        </div>
      ) : (
        <div className="flex min-h-0 min-w-0 max-w-full flex-1 flex-col overflow-hidden lg:flex-row">
          {!wideScreen ? (
            <div className="relative flex shrink-0 bg-panel lg:hidden">
              <motion.div
                aria-hidden
                className="pointer-events-none absolute bottom-0 left-0 z-0 h-[3px] w-1/2 rounded-t-sm bg-accent"
                initial={false}
                animate={{ x: mobileTab === 'canvas' ? '100%' : '0%' }}
                transition={tabIndicatorTransition}
              />
              <button
                type="button"
                onClick={() => setMobileTab('storyboard')}
                className={[
                  'relative z-[1] min-h-11 flex-1 text-sm font-semibold transition-colors',
                  mobileTab === 'storyboard' ? 'text-ink' : 'text-ink-soft hover:text-ink',
                ].join(' ')}
              >
                分镜
              </button>
              <button
                type="button"
                onClick={() => setMobileTab('canvas')}
                className={[
                  'relative z-[1] min-h-11 flex-1 text-sm font-semibold transition-colors',
                  mobileTab === 'canvas' ? 'text-ink' : 'text-ink-soft hover:text-ink',
                ].join(' ')}
              >
                画布
              </button>
            </div>
          ) : null}

          <div className="relative grid min-h-0 min-w-0 flex-1 grid-cols-1 grid-rows-1 overflow-hidden lg:flex lg:min-h-0 lg:flex-row">
            {wideScreen && project ? (
              <aside
                className={[
                  'hidden h-full min-h-0 w-56 shrink-0 flex-col border-line bg-panel/70 lg:flex',
                  angular ? 'border-r-2' : 'border-r',
                ].join(' ')}
              >
                <div className="border-b border-line px-4 py-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="typo-lib-section">话次目录</p>
                    <div className="flex items-center gap-1">
                      <ComicRailDirectoryButton
                        angular={angular}
                        label="调整话次"
                        tooltip="调整话次"
                        onClick={() => setEpisodeDirectoryModalOpen(true)}
                      />
                      <ChromeTooltip content="画册设置" angular={angular} side="bottom" align="end">
                        <ChromeToolbarButton
                          type="button"
                          className="h-8 w-8 !p-0"
                          onClick={() => setCreativeModalOpen(true)}
                          aria-label="画册设置"
                        >
                          <IconPencil className="h-4 w-4" />
                        </ChromeToolbarButton>
                      </ChromeTooltip>
                    </div>
                  </div>
                </div>
                <div className="scroll-region flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto p-2">
                  {project.episodes.map((ep) => {
                    const pageCount = ep.pages?.length ?? 0
                    const isActive = ep.id === selectedEpisodeId
                    return (
                      <button
                        key={ep.id}
                        type="button"
                        className={[
                          'group flex w-full min-w-0 items-center gap-2 px-3 py-2 text-left transition',
                          angular ? 'rounded-none' : 'rounded-lg',
                          isActive ? 'bg-accent font-medium text-white' : 'text-ink hover:bg-surface-muted',
                        ].join(' ')}
                        onClick={() => handleSelectEpisode(ep.id)}
                        title={ep.title}
                      >
                        <span className="min-w-0 flex-1 truncate text-sm">{ep.title}</span>
                        <span className={['shrink-0 text-xs tabular-nums', isActive ? 'text-white/80' : 'text-ink-soft'].join(' ')}>
                          {pageCount} 页
                        </span>
                      </button>
                    )
                  })}
                </div>
                <div className="border-t border-line p-2">
                  <div className="flex items-center justify-center gap-2">
                    {canUpdateProject ? (
                      <ComicRailAddButton
                        angular={angular}
                        label="新建页"
                        tooltip="新建页"
                        onClick={handleAddPage}
                      />
                    ) : null}
                    {canUpdateProject && selectedEpisodeId ? (
                      <ComicRailEditButton
                        angular={angular}
                        label="编辑话次"
                        tooltip="编辑话次"
                        onClick={() => setEpisodeEditOpen(true)}
                      />
                    ) : null}
                    {canUpdateProject && episode && episode.pages.length > 1 && currentPage ? (
                      <ComicRailDeleteButton
                        angular={angular}
                        label={`删除${currentPage.title}`}
                        tooltip="删除当前页"
                        onClick={() => requestDeletePage(currentPage.id)}
                      />
                    ) : null}
                  </div>
                </div>
              </aside>
            ) : null}

            <ThemeLayerCrossfade
              as="aside"
              className={[
                'col-start-1 row-start-1 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-panel',
                'lg:col-auto lg:row-auto lg:h-full lg:max-h-full lg:w-[min(100%,420px)] lg:shrink-0 lg:border-r lg:border-[var(--shell-rail-border)] lg:bg-[var(--shell-rail-bg)]',
              ].join(' ')}
              visible={wideScreen || mobileTab === 'storyboard'}
              initialX={wideScreen ? -16 : 0}
              style={{
                pointerEvents: wideScreen || mobileTab === 'storyboard' ? 'auto' : 'none',
                zIndex: wideScreen ? undefined : mobileTab === 'storyboard' ? 2 : 1,
              }}
            >
              <div
                ref={setStoryboardScrollPort}
                className={[
                  'scroll-region overscroll-y-contain min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden',
                  'ps-4 pe-4 py-3 sm:ps-5 sm:pe-6 sm:py-4 lg:pe-7',
                  comicEditorSelectClass(),
                ].join(' ')}
              >
                <div
                  className="min-w-0 max-w-full will-change-transform"
                  style={{ transform: `translate3d(0, ${storyboardPullPx}px, 0)` }}
                >
                  <div className="select-none" aria-live="polite">
                    {editorRefreshing ||
                    storyboardPullPx >= 8 ||
                    storyboardPullCommitFlying ? (
                      <TimelinePullIndicator
                        pullPx={
                          storyboardPullCommitFlying
                            ? storyboardPullReleaseThreshold
                            : storyboardPullPx
                        }
                        releaseThreshold={storyboardPullReleaseThreshold}
                        angular={angular}
                        commitFly={storyboardPullCommitFlying}
                        onCommitFlyComplete={onStoryboardPullCommitFlyComplete}
                        refreshing={editorRefreshing}
                      />
                    ) : (
                      <div className="h-0 min-h-px shrink-0" aria-hidden />
                    )}
                  </div>
                  {storyboardColumn}
                </div>
              </div>
              {storyboardGenerateBar}
            </ThemeLayerCrossfade>

            <ThemeLayerCrossfade
              as="section"
              className={[
                'relative col-start-1 row-start-1 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-surface',
                'lg:col-auto lg:row-auto',
              ].join(' ')}
              visible={wideScreen || mobileTab === 'canvas'}
              fadeOnly
              style={{
                pointerEvents: wideScreen || mobileTab === 'canvas' ? 'auto' : 'none',
                zIndex: wideScreen ? undefined : mobileTab === 'canvas' ? 2 : 1,
              }}
            >
              <ComicEpisodeCanvasScrollShell>{canvasColumn}</ComicEpisodeCanvasScrollShell>
            </ThemeLayerCrossfade>
          </div>
        </div>
      )}
      {project ? (
        <>
          <ComicCreativeSettingsModal
            open={creativeModalOpen}
            angular={angular}
            project={project}
            clientUi={clientUi}
            saving={saving}
            referenceCap={pageReferenceCap}
            onClose={() => setCreativeModalOpen(false)}
            onSave={handleSaveCreativeSettings}
          />
          <ComicEpisodeDirectoryModal
            open={episodeDirectoryModalOpen}
            angular={angular}
            episodes={project.episodes}
            activeEpisodeId={selectedEpisodeId}
            canvasEpisodeIds={canvasEpisodeIds}
            episodeSaveStatus={episodeSaveStatus}
            onClose={() => setEpisodeDirectoryModalOpen(false)}
            onToggleCanvasEpisode={handleToggleCanvasEpisode}
            onEditEpisode={handleEditEpisode}
          />
          <ComicEpisodeGeneratePagePickModal
            open={episodePagePickEpisodeId != null}
            angular={angular}
            project={project}
            episodeId={episodePagePickEpisodeId}
            onClose={() => setEpisodePagePickEpisodeId(null)}
            onConfirm={(pageIds) => void handleConfirmEpisodePagePick(pageIds)}
          />
          <ThemeModal
            open={workflowAttachOpen}
            onClose={() => setWorkflowAttachOpen(false)}
            title="接入工作流"
            angular={angular}
            size="sm"
            footer={
              <div className="flex justify-end gap-2">
                <ChromeToolbarButton type="button" onClick={() => setWorkflowAttachOpen(false)}>
                  取消
                </ChromeToolbarButton>
              </div>
            }
          >
            <div className="space-y-3">
              <button
                type="button"
                className="block w-full rounded-theme border border-line bg-panel px-4 py-3 text-left transition-colors hover:bg-surface-muted"
                onClick={() => openWorkflowApply('append_episodes')}
              >
                <p className="text-sm font-semibold text-ink">追加后续话</p>
                <p className="mt-1 text-xs leading-relaxed text-ink-soft">保留当前内容，把工作流规划结果作为新话追加到项目末尾。</p>
              </button>
              <button
                type="button"
                className="block w-full rounded-theme border border-line bg-panel px-4 py-3 text-left transition-colors hover:bg-surface-muted disabled:cursor-not-allowed disabled:opacity-60"
                onClick={() => openWorkflowApply('replace_selected_episode')}
                disabled={!selectedEpisodeId}
              >
                <p className="text-sm font-semibold text-ink">覆盖当前话</p>
                <p className="mt-1 text-xs leading-relaxed text-ink-soft">使用工作流结果替换当前选中的话，并重建这一话的分页骨架。</p>
              </button>
              <button
                type="button"
                className="block w-full rounded-theme border border-line bg-panel px-4 py-3 text-left transition-colors hover:bg-surface-muted disabled:cursor-not-allowed disabled:opacity-60"
                onClick={() => openWorkflowApply('fill_empty_pages')}
                disabled={!selectedEpisodeId}
              >
                <p className="text-sm font-semibold text-ink">补空白页</p>
                <p className="mt-1 text-xs leading-relaxed text-ink-soft">仅对当前选中的空白话补入分页与目标，已有内容不会被自动覆盖。</p>
              </button>
            </div>
          </ThemeModal>
          <ComicEpisodeEditModal
            open={episodeEditOpen}
            angular={angular}
            episode={episode}
            onClose={() => setEpisodeEditOpen(false)}
            onSave={(patch) => {
              if (selectedEpisodeId) handleEditEpisode(selectedEpisodeId, patch)
            }}
          />
          <ThemeModal
            open={exportConfirm != null}
            onClose={() => setExportConfirm(null)}
            title={
              exportConfirm?.kind === 'page'
                ? '导出分页'
                : exportConfirm?.kind === 'pdf'
                  ? '导出 PDF'
                  : '导出 ZIP'
            }
            angular={angular}
            footer={
              <div className="flex justify-end gap-2">
                <ChromeToolbarButton type="button" onClick={() => setExportConfirm(null)}>
                  取消
                </ChromeToolbarButton>
                <ChromePrimaryButton
                  density="compact"
                  type="button"
                  onClick={() => {
                    if (!exportConfirm) return
                    const pending = exportConfirm
                    setExportConfirm(null)
                    if (pending.kind === 'page') void handleExportPage(pending.pageId)
                    else if (pending.kind === 'zip') void handleExportEpisode(pending.episodeId)
                    else void handleExportEpisodePdf(pending.episodeId)
                  }}
                >
                  确认导出
                </ChromePrimaryButton>
              </div>
            }
          >
            <p className="text-sm leading-relaxed text-ink">
              {exportConfirm?.kind === 'page' ? (
                <>
                  确定导出分页「<span className="font-medium">{exportConfirm.label}</span>」为 PNG？
                </>
              ) : exportConfirm?.kind === 'pdf' ? (
                <>
                  确定将话次「<span className="font-medium">{exportConfirm.label}</span>」导出为 PDF？
                  无成图的分页将跳过。
                </>
              ) : exportConfirm ? (
                <>
                  确定将话次「<span className="font-medium">{exportConfirm.label}</span>」打包为 ZIP？
                  无成图的分页将跳过。
                </>
              ) : null}
            </p>
          </ThemeModal>
          <ThemeModal
            open={deleteEpisodeId != null}
            onClose={() => setDeleteEpisodeId(null)}
            title="删除话次"
            angular={angular}
            footer={
              <div className="flex justify-end gap-2">
                <ChromeToolbarButton type="button" onClick={() => setDeleteEpisodeId(null)}>
                  取消
                </ChromeToolbarButton>
                <ChromeToolbarDangerButton
                  type="button"
                  className="px-4 py-2"
                  onClick={confirmDeleteEpisode}
                >
                  删除
                </ChromeToolbarDangerButton>
              </div>
            }
          >
            <p className="text-sm text-ink">
              确定删除「
              <span className="font-medium">
                {project.episodes.find((e) => e.id === deleteEpisodeId)?.title ?? '该话'}
              </span>
              」？本话内所有分页、分镜与成图将一并移除，此操作不可恢复。
            </p>
          </ThemeModal>
          <ThemeModal
            open={deletePageId != null}
            onClose={() => setDeletePageId(null)}
            title="删除分页"
            angular={angular}
            footer={
              <div className="flex justify-end gap-2">
                <ChromeToolbarButton type="button" onClick={() => setDeletePageId(null)}>
                  取消
                </ChromeToolbarButton>
                <ChromeToolbarDangerButton
                  type="button"
                  className="px-4 py-2"
                  onClick={confirmDeletePage}
                >
                  删除
                </ChromeToolbarDangerButton>
              </div>
            }
          >
            <p className="text-sm text-ink">
              确定删除「
              <span className="font-medium">
                {episode?.pages.find((p) => p.id === deletePageId)?.title ?? '该页'}
              </span>
              」？页内分镜将前移到后续分页，成图随分镜保留，此操作不可恢复。
            </p>
          </ThemeModal>
          <ThemeModal
            open={deletePanelId != null}
            onClose={() => setDeletePanelId(null)}
            title="删除分镜"
            angular={angular}
            footer={
              <div className="flex justify-end gap-2">
                <ChromeToolbarButton type="button" onClick={() => setDeletePanelId(null)}>
                  取消
                </ChromeToolbarButton>
                <ChromeToolbarDangerButton
                  type="button"
                  className="px-4 py-2"
                  onClick={confirmRemovePanel}
                >
                  删除
                </ChromeToolbarDangerButton>
              </div>
            }
          >
            <p className="text-sm text-ink">
              确定删除「<span className="font-medium">{deletePanelLabel || '该分镜'}</span>」？格内脚本、台词与候选成图将一并移除，此操作不可恢复。
            </p>
          </ThemeModal>
          <ComicPageCandidatesModal
            open={pageCandidatesPageId != null}
            angular={angular}
            panelAspect={getProjectPanelAspect(project)}
            page={pageCandidatesPage}
            applyLabel={episodeQueue ? '应用并继续' : '应用为整页'}
            onClose={() => setPageCandidatesPageId(null)}
            onSelect={(candidateId) => {
              handleApplyPageCandidate(candidateId, pageCandidatesPageId ?? undefined)
            }}
            onEditCandidate={(candidateId) => {
              if (pageCandidatesPageId) {
                handleRequestEditPageImage(pageCandidatesPageId, candidateId)
              }
            }}
          />
          <ComicPageEditImageModal
            open={pageEditTarget != null}
            angular={angular}
            pageTitle={pageEditPage?.title ?? '未命名页'}
            page={pageEditPage}
            initialCandidateId={pageEditTarget?.initialCandidateId ?? null}
            submitting={pageEditSubmitting}
            onClose={() => setPageEditTarget(null)}
            onConfirm={(payload) => void handleConfirmPageEditImage(payload)}
          />
          <ComicPanelCandidatesModal
            open={candidatesPanelId != null}
            angular={angular}
            panel={candidatesPanel}
            panelAspectClass={panelAspectTailwindClass(getProjectPanelAspect(project))}
            onClose={() => setCandidatesPanelId(null)}
            onSelect={(candidateId) => {
              handleSelectPanelCandidate(candidateId, candidatesPanelId ?? undefined)
              setCandidatesPanelId(null)
            }}
            onRemove={(candidateId) => {
              if (!project || !episode || !candidatesPanelId) return
              applyProject(
                removePanelCandidate(project, episode.id, candidatesPanelId, candidateId),
                { save: true },
              )
            }}
          />
          <ComicProjectReaderModal
            open={projectReaderOpen}
            angular={angular}
            project={project}
            onClose={() => setProjectReaderOpen(false)}
            onNavigate={(episodeId, pageId, panelId) => {
              if (episodeId !== selectedEpisodeId) {
                handleSelectEpisode(episodeId)
              }
              handleSelectPage(pageId)
              if (panelId) {
                const ep = project.episodes.find((e) => e.id === episodeId)
                if (ep) selectPanel(panelId, ep)
              }
              setProjectReaderOpen(false)
              if (!wideScreen) setMobileTab(panelId ? 'storyboard' : 'canvas')
            }}
          />
          {webtoonReaderEpisode ? (
            <ComicWebtoonReaderModal
              open={webtoonReaderEpisodeId != null}
              angular={angular}
              project={project}
              episode={webtoonReaderEpisode}
              onClose={() => setWebtoonReaderEpisodeId(null)}
              onSelectPage={(pageId) => {
                if (webtoonReaderEpisode.id !== selectedEpisodeId) {
                  handleSelectEpisode(webtoonReaderEpisode.id)
                }
                handleSelectPage(pageId)
                setWebtoonReaderEpisodeId(null)
                if (!wideScreen) setMobileTab('canvas')
              }}
              onSelectPanel={(panelId) => {
                selectPanel(panelId, webtoonReaderEpisode)
                setWebtoonReaderEpisodeId(null)
                if (!wideScreen) setMobileTab('storyboard')
              }}
            />
          ) : null}
        </>
      ) : null}
    </ContentSkeleton>
  )
}
